import React, { useEffect } from "react";
import QuestionAnswer from "./QuestionAnswer";
import Askquestion from "./Askquestion";
import { useBaseChatLogic } from "./BaseChatComponent";
import type { ChatMessage } from "./BaseChatComponent";
import axios from "axios";
import CommonValues from "../Common/utils";
import { useLocation } from "react-router-dom";

interface SupportChatComponentProps {
  showWideSidebar: boolean;
  showHistory: boolean;
  onOpenDocumentClick: () => void;
  activeMode: "support" | "call";
  setActiveMode: (mode: "support" | "call") => void;
}

export default function SupportChatComponent({
  showWideSidebar,
  showHistory,
  onOpenDocumentClick,
  activeMode,
  setActiveMode,
}: SupportChatComponentProps) {
  const {
    showQuestionAnswerBlock,
    setShowQuestionAnswerBlock,
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    conversationId,
    setConversationId,
    paramConversationId,
    navigate,
    handleFollowUpQuestion,
    handlePromptClick,
  } = useBaseChatLogic();

  const fetchSupportChatHistory = async (conversationId: string) => {
    try {
      const token = localStorage.getItem("igtoken");
      const response = await axios.get(
        `${process.env.REACT_APP_NCSupportBot_API}/api/ncsupportbot/nc-support-bot-conversation-history/${conversationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      const chatHistory: ChatMessage[] = response.data.map((item: any) => ({
        question: item.question,
        answer: item.response,
        documents: item.documents || [],
        sources: item.sources || [],
        chatId: item.chatId || "",
        conversationId: conversationId,
      }));
      setMessages(chatHistory);
      setShowQuestionAnswerBlock(true);
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/");
        CommonValues.Logout(navigate);
      }
    }
  };

  useEffect(() => {
    if (paramConversationId) {
      setConversationId(paramConversationId);
      fetchSupportChatHistory(paramConversationId);
    }
  }, [paramConversationId, navigate]);

  const handleSendSupportMessage = async (message: string) => {
    setIsLoading(true);
    setShowQuestionAnswerBlock(true);

    const newMessage: ChatMessage = {
      question: message,
      answer: "Loading...",
      documents: [],
      sources: [],
      conversationId: conversationId,
    };
    setMessages((prev) => [...prev, newMessage]);
    const updatedMessages = [...messages, newMessage];

    let retryCount = 0;
    const maxRetries = 3;

    while (retryCount < maxRetries) {
      try {
        const token = localStorage.getItem("igtoken");
        const response = await fetch(
          `${process.env.REACT_APP_SupportBot_API}/chat`,
          {
            method: "POST",
            headers: {
              authorization: `Bearer ${token}`,
              "content-type": "application/json",
            },
            body: JSON.stringify({
              ConversationId: conversationId,
              ChatHistory: updatedMessages.map((msg) => ({
                Question: msg.question,
                Answer: msg.answer === "Loading..." ? "" : msg.answer,
              })),
              Question: message,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No reader available");

        let answer = "";
        let documents: string[] = [];
        let sources: string[] = [];
        let chatId: string | undefined;
        let hasReceivedData = false;
        let isFirstAnswerChunk = true;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = new TextDecoder().decode(value);
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;

            const jsonStr = line.slice(6);
            if (
              !jsonStr.trim() ||
              jsonStr.trim() === "[DONE]" ||
              jsonStr.trim() === "{}"
            )
              continue;

            try {
              const data = JSON.parse(jsonStr);
              hasReceivedData = true;

              if (data.Answer) {
                if (isFirstAnswerChunk) {
                  answer = "";
                  isFirstAnswerChunk = false;
                }

                const cleanChunk = data.Answer.replace(
                  /\$\$PARENTDOMIDSTART/g,
                  ""
                );

                for (let i = 0; i < cleanChunk.length; i++) {
                  await new Promise((resolve) => setTimeout(resolve, 10));
                  answer += cleanChunk[i];
                  setMessages((prev) => {
                    const copy = [...prev];
                    copy[copy.length - 1].answer = answer;
                    return copy;
                  });
                }
              }

              if (data.Documents) {
                documents = data.Documents;
                setMessages((prev) => {
                  const copy = [...prev];
                  copy[copy.length - 1].documents = documents;
                  return copy;
                });
              }

              if (data.Sources) {
                sources = data.Sources;
                setMessages((prev) => {
                  const copy = [...prev];
                  copy[copy.length - 1].sources = sources;
                  return copy;
                });
              }

              if (data.ChatId) {
                chatId = data.ChatId;
                setMessages((prev) => {
                  const copy = [...prev];
                  copy[copy.length - 1].chatId = chatId;
                  return copy;
                });
              }
            } catch (err) {
              console.error("Error parsing stream JSON:", err, "Line:", line);
            }
          }
        }

        if (hasReceivedData) {
          setMessages((prev) => {
            const copy = [...prev];
            const last = copy[copy.length - 1];
            last.answer =
              answer ||
              "I apologize, but I couldn't process your request at this time.";
            last.documents = documents;
            last.sources = sources;
            last.chatId = chatId;
            return copy;
          });
          break;
        } else {
          throw new Error("Stream ended without any valid data");
        }
      } catch (err) {
        console.error("Request failed:", err);
        retryCount++;
        if (retryCount >= maxRetries) {
          setMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1].answer =
              "I apologize, but I encountered an error while processing your request.";
            return copy;
          });
        } else {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }
    }
    setIsLoading(false);
  };

  const supportPromptCards = [
    {
      label: "Certificate of Insurance",
      title: "How to send a Certificate of Insurance ?",
    },
    {
      label: "Access and Permissions",
      title: "How to manage user access and permissions?",
    },
    {
      label: "Generate Reports ",
      title: "How to generate reports and export data?",
    },
    {
      label: "Policies",
      title: "How to edit, delete, or merge policies and accounts?",
    },
    { label: "Payments", title: "How to handle commissions and payments?" },
  ];

  const location = useLocation();
  
  useEffect(() => {
    if (location.state?.category) {
      setActiveMode(location.state.category);
    }
  }, [location.state?.category]);
  

  return (
    <>
      {showQuestionAnswerBlock ? (
        <div
          className={
            showWideSidebar
              ? "chat-question-large-width"
              : "chat-question-small-width"
          }
        >
          <QuestionAnswer
            messages={messages}
            onOpenDocumentClick={onOpenDocumentClick}
            onFollowUpQuestionClick={(q) =>
              handleFollowUpQuestion(q, handleSendSupportMessage)
            }
          />
        </div>
      ) : (
        <>
          <div className="text-center my-4 mt-5">
            <h5 className="fw-semibold mb-3">
              Support - What can I help with?
            </h5>
            <p className="text-muted small">
              Welcome to Support! Here's what's happening with your knowledge
              base.
            </p>
          </div>
          <div className="container my-5 mt-5">
            <div className="row justify-content-center g-2 mt-5 mb-5">
              {supportPromptCards.map((item, idx) => (
                <div
                  key={idx}
                  className="col-12 col-sm-6 col-md-3 d-flex justify-content-center"
                >
                  <div
                    className="prompt-card d-flex flex-column justify-content-center small-card"
                    onClick={() =>
                      handlePromptClick(item.title, handleSendSupportMessage)
                    }
                    style={{
                      cursor: "pointer",
                      width: "100%",
                      maxWidth: "300px",
                    }}
                  >
                    <div>
                      <span className="badge">{item.label}</span>
                      <div className="card-title">{item.title}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div
        className={`mt-auto pt-4 ${
          showWideSidebar
            ? "chat-question-large-block"
            : "chat-question-small-block"
        }`}
      >
        <div
          className={
            showHistory
              ? "main-historycontent-large-width"
              : "main-historycontent-small-width"
          }
        >
          <div className="px-3 py-1">
            <div>
              <div className="row mb-1 d-flex align-items-center">
                <div className="col-6 d-flex align-items-center"></div>
              </div>
              <Askquestion
                onSendMessage={handleSendSupportMessage}
                isLoading={isLoading}
              />
            </div>
          </div>
          <small className="text-muted d-block text-center text-12">
            AI-generated content may be incorrect
          </small>
          <div className="d-flex justify-content-center my-2 gap-2">
            <button
              className={`custom-btn btn-sm ${
                activeMode === "support" ? "active-support" : "outline-support"
              }`}
              onClick={() => setActiveMode("support")}
            >
              Support
            </button>
            <button
              className={`custom-btn btn-sm ${
                activeMode === "call" ? "active-call" : "outline-call"
              }`}
              onClick={() => setActiveMode("call")}
            >
              Call
            </button>
          </div>
        </div>
      </div>
    </>
  );
}